import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AmiraResources } from '@/models/AmiraResources.interface';
import { RolloverRecommendationAction } from '@/features/planner/enums/RolloverRecommendation';

interface ResourceModalContextType {
  selectedResource: AmiraResources | null;
  selectedAction: RolloverRecommendationAction | null;
  studentId: string | null;
  skillId: string | null;
  isModalOpen: boolean;
  openResourceModal: (
    resource: AmiraResources, 
    action: RolloverRecommendationAction,
    studentId: string,
    skillId: string
  ) => void;
  closeResourceModal: () => void;
}

const ResourceModalContext = createContext<ResourceModalContextType | undefined>(undefined);

interface ResourceModalProviderProps {
  children: ReactNode;
}

export const ResourceModalProvider: React.FC<ResourceModalProviderProps> = ({ children }) => {
  const [selectedResource, setSelectedResource] = useState<AmiraResources | null>(null);
  const [selectedAction, setSelectedAction] = useState<RolloverRecommendationAction | null>(null);
  const [studentId, setStudentId] = useState<string | null>(null);
  const [skillId, setSkillId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openResourceModal = (
    resource: AmiraResources,
    action: RolloverRecommendationAction,
    studentId: string,
    skillId: string
  ) => {
    setSelectedResource(resource);
    setSelectedAction(action);
    setStudentId(studentId);
    setSkillId(skillId);
    setIsModalOpen(true);
  };

  const closeResourceModal = () => {
    setSelectedResource(null);
    setSelectedAction(null);
    setStudentId(null);
    setSkillId(null);
    setIsModalOpen(false);
  };

  const value: ResourceModalContextType = {
    selectedResource,
    selectedAction,
    studentId,
    skillId,
    isModalOpen,
    openResourceModal,
    closeResourceModal,
  };

  return (
    <ResourceModalContext.Provider value={value}>
      {children}
    </ResourceModalContext.Provider>
  );
};

export const useResourceModal = (): ResourceModalContextType => {
  const context = useContext(ResourceModalContext);
  if (context === undefined) {
    throw new Error('useResourceModal must be used within a ResourceModalProvider');
  }
  return context;
}; 